import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { Badge, Banner, Card, Pager, PanelState } from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { useRevoke } from "../lib/useRevoke";
import { date, device, num, relative } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { PageMeta, SessionRow } from "../lib/types";

/**
 * ── The stolen-session console ─────────────────────────────────────────────
 * This page is the reason `Session` is a table rather than a signed JWT. A
 * stateless token cannot be withdrawn — it stays valid until it expires, and
 * the only lever is rotating the signing secret, which signs every customer
 * out at once. Here, one row's token is dead on its next request.
 */
export function SessionsPage() {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [page, setPage] = useState(1);
  const { canRevoke } = useAuth();

  const { data, error, loading, stale, reload } = useApi<{
    sessions: SessionRow[];
    pagination: PageMeta;
  }>("/api/admin/sessions", {
    page,
    pageSize: 50,
    includeRevoked: includeRevoked ? "1" : undefined,
  });

  const { revokeSession, revokeUser, busyId, toast } = useRevoke(reload);

  return (
    <Page
      title="Sessions"
      subtitle={
        data
          ? `${num(data.pagination.total)} matching sessions`
          : "Loading…"
      }
      actions={
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => {
              setIncludeRevoked(e.target.checked);
              setPage(1);
            }}
          />
          Show ended sessions
        </label>
      }
    >
      {!canRevoke && (
        <Banner title="Read-only">
          Your role is Analyst, so you can see the session estate but not end
          anything. Ask an owner or admin to revoke a session.
        </Banner>
      )}

      <Card
        title="Signed-in devices"
        note="Newest activity first. Ending a session takes effect on that device's very next request — the customer is not notified and will need a new OTP."
      >
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.sessions.length === 0}
            emptyText="No sessions to show."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Device</th>
                    <th>Address</th>
                    <th>Last seen</th>
                    <th>Signed in</th>
                    <th>Status</th>
                    {canRevoke && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data?.sessions.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link
                          to={`/customers/${s.userId}`}
                          className="cell-strong"
                          style={{ textDecoration: "underline" }}
                        >
                          {s.user?.fullName ?? "Unnamed"}
                        </Link>
                        <div className="cell-mute mono">
                          {s.user?.phone ?? s.userId}
                        </div>
                      </td>
                      <td>{device(s.userAgent)}</td>
                      <td className="mono cell-mute">{s.ip ?? "—"}</td>
                      <td className="cell-mute">{relative(s.lastSeenAt)}</td>
                      <td className="cell-mute">{date(s.createdAt)}</td>
                      <td>
                        {s.live ? (
                          <Badge tone="good">Live</Badge>
                        ) : s.revokedAt ? (
                          <Badge>Ended {relative(s.revokedAt)}</Badge>
                        ) : (
                          <Badge>Expired</Badge>
                        )}
                      </td>
                      {canRevoke && (
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {s.live && (
                            <>
                              <button
                                className="btn btn-sm btn-danger"
                                disabled={busyId === s.id}
                                onClick={() =>
                                  void revokeSession(
                                    s.id,
                                    `${s.user?.fullName ?? "Unnamed"} · ${device(s.userAgent)} · ${s.ip ?? "unknown address"}`,
                                  )
                                }
                              >
                                {busyId === s.id ? "Ending…" : "End"}
                              </button>{" "}
                              <button
                                className="btn btn-sm btn-danger"
                                disabled={busyId === s.userId}
                                onClick={() =>
                                  void revokeUser(
                                    s.userId,
                                    s.user?.fullName ?? "this customer",
                                    "sessions page",
                                  )
                                }
                                title="Sign this customer out of every device"
                              >
                                All
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="sessions"
            />
          </PanelState>
        </div>
      </Card>

      <p className="card-note">
        Addresses are only as good as <span className="mono">TRUST_PROXY</span>{" "}
        on the API. Behind a load balancer with it unset, every row records the
        balancer's address rather than the customer's.
      </p>
      {toast}
    </Page>
  );
}
